/**
 * P2 Knockdown prone — pure math.
 *
 * State-driven persistent animation:
 *
 *   - On rising edge (mech.knocked_down false → true): chassis pitches
 *     forward (rotation.x toward `KNOCKDOWN_PITCH_RAD`) over
 *     `KNOCKDOWN_FALL_MS` with a slight bounce-out easing for the
 *     "thump" landing.
 *   - While prone: held at the pitched angle with a tiny dip below
 *     base Y so the chassis reads as resting on the floor.
 *   - On falling edge: chassis pitches back to 0 over
 *     `KNOCKDOWN_RECOVER_MS` (linear ease — slower than the fall to
 *     read as "getting back up").
 *
 * Pure math; the caller (ArchetypeMech) tracks the rising/falling-edge
 * timer and feeds elapsed ms here.
 */

/** Peak forward pitch in the prone state (radians). ~1.22 rad ≈ 70°. */
export const KNOCKDOWN_PITCH_RAD = 1.22;
/** Time to fall to prone after the rising edge. */
export const KNOCKDOWN_FALL_MS = 320;
/** Time to recover to upright after the falling edge. */
export const KNOCKDOWN_RECOVER_MS = 480;
/** Y dip below base while prone (world units). */
export const KNOCKDOWN_SAG_Y = 0.06;

export interface KnockdownState {
  pitchX: number;
  sagY: number;
}

const UPRIGHT: KnockdownState = { pitchX: 0, sagY: 0 };

/**
 * Cosine-eased value 0..1 from raw t 0..1.
 */
function easeInOut(t: number): number {
  return 0.5 * (1 - Math.cos(Math.min(1, Math.max(0, t)) * Math.PI));
}

/**
 * Resolve current knockdown pitch + sag.
 *
 * @param prone           Current `mech.knocked_down`.
 * @param fallElapsedMs   ms since the rising edge (null if never).
 * @param recoverElapsedMs ms since the falling edge (null if never or
 *                        still prone).
 */
export function knockdownState(
  prone: boolean,
  fallElapsedMs: number | null,
  recoverElapsedMs: number | null,
): KnockdownState {
  if (prone) {
    // Falling or holding prone.
    if (fallElapsedMs === null) return UPRIGHT;
    if (fallElapsedMs >= KNOCKDOWN_FALL_MS) {
      return { pitchX: KNOCKDOWN_PITCH_RAD, sagY: -KNOCKDOWN_SAG_Y };
    }
    const t = fallElapsedMs / KNOCKDOWN_FALL_MS;
    const e = easeInOut(t);
    return { pitchX: KNOCKDOWN_PITCH_RAD * e, sagY: -KNOCKDOWN_SAG_Y * e };
  }
  // Recovering or upright.
  if (recoverElapsedMs === null) return UPRIGHT;
  if (recoverElapsedMs >= KNOCKDOWN_RECOVER_MS) return UPRIGHT;
  const t = recoverElapsedMs / KNOCKDOWN_RECOVER_MS;
  const e = 1 - easeInOut(t); // ease pitch back down
  return { pitchX: KNOCKDOWN_PITCH_RAD * e, sagY: -KNOCKDOWN_SAG_Y * e };
}
